/**
 * Computes the section polygon (centered at the section centroid, CCW order)
 * for the supported section types. The frontend mirrors the backend's
 * `Section.polygon()` so the 3D viewer can extrude the real shape.
 *
 * All coordinates in cm; origin at the section centroid (matches the rebar
 * positions returned by the backend).
 */

import type { SectionType } from './types';

export type Polygon = readonly (readonly [number, number])[];

export function getSectionPolygon(
  sectionType: SectionType,
  L1: number,
  L2: number,
  e1: number,
  e2: number,
): Polygon {
  if (sectionType === 'rectangular') {
    const hx = L1 / 2;
    const hy = L2 / 2;
    return [
      [-hx, -hy],
      [+hx, -hy],
      [+hx, +hy],
      [-hx, +hy],
    ];
  }
  if (sectionType === 'L') {
    const a1 = L1 * e1;
    const a2 = e2 * (L2 - e1);
    const at = a1 + a2;
    const cx = (a1 * (L1 / 2) + a2 * (e2 / 2)) / at;
    const cy = (a1 * (e1 / 2) + a2 * (e1 + (L2 - e1) / 2)) / at;
    // Raw vertices CCW from lower-left, then center
    const raw: [number, number][] = [
      [0, 0],
      [L1, 0],
      [L1, e1],
      [e2, e1],
      [e2, L2],
      [0, L2],
    ];
    return raw.map(([x, y]) => [x - cx, y - cy] as const);
  }
  if (sectionType === 'circular') {
    // Circular: L1 is the diameter; L2 ignored. Approximate by 48-sided polygon.
    const N = 48;
    const r = L1 / 2;
    const pts: [number, number][] = [];
    for (let i = 0; i < N; i++) {
      const a = (2 * Math.PI * i) / N;
      pts.push([r * Math.cos(a), r * Math.sin(a)]);
    }
    return pts;
  }
  if (sectionType === 'cruciforme') {
    // + shape: horizontal arm (L1 × e1) + vertical arm (e2 × L2), crossed.
    // Doubly symmetric ⇒ centroid at (L1/2, L2/2).
    const cx = L1 / 2;
    const cy = L2 / 2;
    const xwL = (L1 - e2) / 2;
    const xwR = (L1 + e2) / 2;
    const yhB = (L2 - e1) / 2;
    const yhT = (L2 + e1) / 2;
    const raw: [number, number][] = [
      [xwL, 0],
      [xwR, 0],
      [xwR, yhB],          // re-entrant
      [L1, yhB],
      [L1, yhT],
      [xwR, yhT],          // re-entrant
      [xwR, L2],
      [xwL, L2],
      [xwL, yhT],          // re-entrant
      [0, yhT],
      [0, yhB],
      [xwL, yhB],          // re-entrant
    ];
    return raw.map(([x, y]) => [x - cx, y - cy] as const);
  }
  if (sectionType === 'T') {
    // T: horizontal flange (L1 × e1) at the top, vertical web (e2 × (L2-e1))
    // centered horizontally below it. Symmetric about y-axis ⇒ cx = L1/2.
    const aFl = L1 * e1;                  // flange area
    const aWe = e2 * (L2 - e1);           // web area
    const at = aFl + aWe;
    const cx = L1 / 2;
    const yFlange = L2 - e1 / 2;
    const yWeb = (L2 - e1) / 2;
    const cy = (aFl * yFlange + aWe * yWeb) / at;
    const xwL = (L1 - e2) / 2;            // left face of web
    const xwR = (L1 + e2) / 2;            // right face of web
    const yfB = L2 - e1;                  // bottom of flange
    // Raw vertices CCW starting from web bottom-left
    const raw: [number, number][] = [
      [xwL, 0],
      [xwR, 0],
      [xwR, yfB],          // re-entrant (270° inside the concrete)
      [L1, yfB],
      [L1, L2],
      [0, L2],
      [0, yfB],
      [xwL, yfB],          // re-entrant
    ];
    return raw.map(([x, y]) => [x - cx, y - cy] as const);
  }
  // Other types not yet implemented; fall back to bbox.
  const hx = L1 / 2;
  const hy = L2 / 2;
  return [
    [-hx, -hy],
    [+hx, -hy],
    [+hx, +hy],
    [-hx, +hy],
  ];
}

/**
 * Returns the inset (offset towards interior) polygon useful as the stirrup
 * centerline. Algorithm: for each edge, displace it inward by `inset` along
 * its inward normal; then for each pair of consecutive displaced edges,
 * intersect their lines and take that intersection as the new vertex.
 *
 * This approach correctly handles convex AND re-entrant (270°) vertices —
 * which is what an L section has at its inner corner.
 */
export function getInsetPolygon(poly: Polygon, inset: number): Polygon {
  const n = poly.length;
  // Compute the inward-displaced line for each edge i: edge from poly[i] to poly[i+1].
  type Line = { px: number; py: number; dx: number; dy: number };
  const lines: Line[] = [];
  for (let i = 0; i < n; i++) {
    const a = poly[i]!;
    const b = poly[(i + 1) % n]!;
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const len = Math.hypot(dx, dy) || 1;
    // Inward normal for CCW polygon: rotate edge direction +90° (left) = (-dy, dx) / len.
    // (The polygon is traversed CCW so the interior is to the LEFT of each edge.)
    const nx = -dy / len;
    const ny = dx / len;
    lines.push({
      px: a[0] + nx * inset,
      py: a[1] + ny * inset,
      dx,
      dy,
    });
  }
  // For each pair of consecutive displaced lines (i-1, i), intersect them to
  // get the new vertex i.
  const out: [number, number][] = [];
  for (let i = 0; i < n; i++) {
    const lp = lines[(i - 1 + n) % n]!;
    const lc = lines[i]!;
    // Solve: lp.p + t * lp.d = lc.p + s * lc.d
    // 2 unknowns t, s. Use Cramer's rule on the 2x2 system.
    const det = -lp.dx * lc.dy + lp.dy * lc.dx;
    if (Math.abs(det) < 1e-9) {
      // Parallel/colinear edges — fall back to lp's endpoint.
      out.push([lp.px + lp.dx, lp.py + lp.dy]);
      continue;
    }
    const t = (-(lc.px - lp.px) * lc.dy + (lc.py - lp.py) * lc.dx) / det;
    const x = lp.px + t * lp.dx;
    const y = lp.py + t * lp.dy;
    out.push([x, y]);
  }
  return out;
}
